import React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    Grid2,
    InputAdornment,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import { PASSWORD_CHANGE_SOURCE } from '../helpers/passwordAuditLogHelpers';
import { formatDateTimeInVenueTz } from '../../../helpers/formatDateTime';

const EditUserSecurityTab = ({
    t,
    onForceSetRandomPassword,
    forceSetUserPasswordMutation,
    generatedPassword,
    onCopyGeneratedPassword,
    lastPasswordChangedAt,
    lastPasswordChangedBy,
    lastPasswordChangeSource,
    isTwoFactorEnabled,
    onResetTwoFactor,
    isResettingTwoFactor,
    canManagePasswords,
}) => {
    return (
        <>
            {canManagePasswords && (
                <Paper sx={{ overflow: 'hidden', mb: 3 }}>
                    <Box sx={{ p: 2, pb: 2 }}>
                        <Box
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                mb: 0.5,
                            }}
                        >
                            <Typography
                                variant="subtitle1"
                                sx={{ textAlign: 'left', fontWeight: 'bold' }}
                            >
                                {t('users.passwordOverride.title')}
                            </Typography>
                            <Chip
                                label={t('users.passwordOverride.statusActive')}
                                size="small"
                                sx={{
                                    fontWeight: 700,
                                    bgcolor: '#dff6e7',
                                    color: '#23764b',
                                }}
                            />
                        </Box>
                        <Box sx={{ textAlign: 'left' }}>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontWeight: 500 }}
                            >
                                {t('users.passwordOverride.subtitle')}
                            </Typography>
                        </Box>
                    </Box>
                    <Divider />
                    <Box sx={{ p: 2 }}>
                        <Alert
                            icon={<WarningAmberRoundedIcon fontSize="small" />}
                            severity="warning"
                            sx={{
                                mb: 3,
                                bgcolor: '#fff9e8',
                                border: '1px solid #f1df9b',
                                color: '#9a6700',
                                '& .MuiAlert-icon': {
                                    color: '#d6a500',
                                },
                                '& .MuiAlert-message': {
                                    width: '100%',
                                    textAlign: 'left',
                                },
                            }}
                        >
                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                                {t('users.passwordOverride.warningTitle')}
                            </Typography>
                            <Typography variant="body2">
                                {t('users.passwordOverride.warning')}
                            </Typography>
                        </Alert>
                        <Box
                            sx={{
                                p: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                bgcolor: 'grey.50',
                                mb: 3,
                            }}
                        >
                            <Grid2 container spacing={2} alignItems="center">
                                <Grid2 size={{ xs: 12, sm: 6 }}>
                                    <Typography variant="subtitle2" sx={{ textAlign: 'left' }}>
                                        {t('users.passwordOverride.forceResetTitle')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{ textAlign: 'left', fontWeight: 500 }}
                                    >
                                        {t('users.passwordOverride.forceResetDescription')}
                                    </Typography>
                                </Grid2>
                                <Grid2 size={{ xs: 12, sm: 6 }} sx={{ textAlign: 'left' }}>
                                    <Button
                                        variant="contained"
                                        onClick={onForceSetRandomPassword}
                                        disabled={forceSetUserPasswordMutation.isPending}
                                        startIcon={<AutorenewRoundedIcon />}
                                        sx={{
                                            width: { xs: 'auto', sm: '100%' },
                                            maxWidth: '100%',
                                            minHeight: 56,
                                            py: 1.1,
                                            px: 3,
                                            fontWeight: 700,
                                            whiteSpace: 'nowrap',
                                            bgcolor: '#1f2c43',
                                            '&:hover': { bgcolor: '#172235' },
                                            '& .MuiButton-startIcon': { mr: 1 },
                                        }}
                                    >
                                        {t('users.passwordOverride.generateNewButton')}
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </Box>
                        <Grid2 container spacing={0}>
                            <Grid2 size={{ xs: 12 }}>
                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 1,
                                        textAlign: 'left',
                                        fontWeight: 700,
                                    }}
                                >
                                    {t('users.passwordOverride.generatedPassword')}
                                </Typography>
                            </Grid2>
                            <Grid2 size={{ xs: 12 }}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        value={generatedPassword}
                                        placeholder=""
                                        variant="outlined"
                                        slotProps={{
                                            input: {
                                                readOnly: true,
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <LockRoundedIcon
                                                            fontSize="medium"
                                                            sx={{
                                                                color: 'text.secondary',
                                                            }}
                                                        />
                                                    </InputAdornment>
                                                ),
                                            },
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                height: 56,
                                                bgcolor: '#f9fafb',
                                                borderTopRightRadius: 0,
                                                borderBottomRightRadius: 0,
                                                '& fieldset': {
                                                    borderColor: 'divider',
                                                },
                                                '&:hover fieldset': {
                                                    borderColor: 'text.secondary',
                                                },
                                                '&.Mui-focused fieldset': {
                                                    borderColor: '#1f2c43',
                                                    borderWidth: '1px',
                                                },
                                            },
                                        }}
                                    />
                                    <Button
                                        onClick={onCopyGeneratedPassword}
                                        variant="outlined"
                                        disabled={!generatedPassword}
                                        startIcon={<ContentCopyRoundedIcon fontSize="small" />}
                                        sx={{
                                            height: 56,
                                            borderTopLeftRadius: 0,
                                            borderBottomLeftRadius: 0,
                                            minWidth: 112,
                                            borderColor: 'divider',
                                            borderLeft: 0,
                                            color: 'text.secondary',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {t('users.passwordOverride.copyShort')}
                                    </Button>
                                </Box>
                            </Grid2>
                            {generatedPassword && (
                                <Grid2 size={{ xs: 12 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            display: 'block',
                                            mt: 1,
                                            textAlign: 'left',
                                            color: '#1b8f43',
                                            fontWeight: 600,
                                        }}
                                    >
                                        {t('users.passwordOverride.successInfo')}
                                    </Typography>
                                </Grid2>
                            )}
                        </Grid2>
                    </Box>
                    <Divider />
                    <Box sx={{ bgcolor: 'grey.50', p: 2 }}>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                display: 'block',
                                textAlign: 'left',
                            }}
                        >
                            {lastPasswordChangedAt &&
                            lastPasswordChangeSource === PASSWORD_CHANGE_SOURCE.FORGOTTEN_PASSWORD
                                ? t('users.passwordOverride.lastChangedByForgottenPassword', {
                                      date: formatDateTimeInVenueTz(lastPasswordChangedAt),
                                      name: lastPasswordChangedBy,
                                  })
                                : lastPasswordChangedAt
                                  ? t('users.passwordOverride.lastChangedBy', {
                                        date: formatDateTimeInVenueTz(lastPasswordChangedAt),
                                        name: lastPasswordChangedBy,
                                    })
                                  : t('users.passwordOverride.lastChangedNone')}
                        </Typography>
                    </Box>
                </Paper>
            )}
            <Paper sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: 2, pb: 2 }}>
                    <Box
                        sx={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 0.5,
                        }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{ textAlign: 'left', fontWeight: 'bold' }}
                        >
                            {t('users.resetTwoFactor.title')}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'left' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                            {t('users.resetTwoFactor.subtitle')}
                        </Typography>
                    </Box>
                </Box>
                <Divider />
                <Box sx={{ p: 2 }}>
                    <Box
                        sx={{
                            p: 2,
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1.5,
                            bgcolor: 'grey.50',
                            mb: 3,
                        }}
                    >
                        <Grid2 container spacing={2} alignItems="center">
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Typography variant="subtitle2" sx={{ textAlign: 'left' }}>
                                    {t('users.resetTwoFactor.forceResetTitle')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ textAlign: 'left', fontWeight: 500 }}
                                >
                                    {t('users.resetTwoFactor.forceResetDescription')}
                                </Typography>
                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 6 }}>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={onResetTwoFactor}
                                    disabled={!isTwoFactorEnabled || isResettingTwoFactor}
                                    startIcon={<AutorenewRoundedIcon />}
                                    sx={{
                                        height: 56,
                                        py: 1.1,
                                        fontWeight: 700,
                                        bgcolor: '#1f2c43',
                                        '&:hover': { bgcolor: '#172235' },
                                    }}
                                >
                                    {t('users.resetTwoFactor.button')}
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Box>
                </Box>
            </Paper>
        </>
    );
};

export default EditUserSecurityTab;
